// Parity P5 (hybrid design §6.1): the rule builder — When / If / Then — in one dialog, and
// "Describe it": a sentence the API drafts into the same shape (what it understood fills the
// builder; what it did not is listed for the person to finish). Every picker is one of the rule's
// typed fields; the API checks what they name (statuses of the board, open seats, boards the
// writer edits) and answers with the field at fault. Saving a change sends the version it was
// opened at (If-Match), so a rule someone changed meanwhile is not overwritten.
import type { OperationInput } from "@companyos/sdk";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Textarea,
  toast,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusIcon, SparklesIcon, XIcon } from "lucide-react";
import { type FormEvent, useEffect, useId, useState } from "react";
import type { Api } from "../../api/client";
import { channelsQuery } from "../chat/chat-queries";
import { type CompanyAccess, isManager } from "../company";
import { metricsQuery, seatsQuery } from "../queries";
import { problemText } from "../settings/problem-text";
import { tagsQuery } from "../work/tags";
import { boardsQuery, fieldsQuery } from "../work/work-queries";
import { processKeys, type Rule } from "./automation-queries";

type Spec = OperationInput<"automations.create">["body"];
type Trigger = Spec["trigger"];
type Condition = Spec["conditions"][number];
type Action = Spec["actions"][number];

export const PRIORITIES = ["None", "Urgent", "High", "Normal", "Low"] as const;

const TRIGGERS: { type: Trigger["type"]; label: string }[] = [
  { type: "task.created", label: "A task is created" },
  { type: "task.moved", label: "A task is moved onto the board" },
  { type: "task.status_changed", label: "A task's status changes" },
  { type: "task.completed", label: "A task is completed" },
  { type: "task.commented", label: "Someone comments on a task" },
  { type: "task.tagged", label: "A task is tagged" },
  { type: "task.field_changed", label: "A field changes" },
  { type: "task.due_soon", label: "A task is due soon" },
  { type: "task.overdue", label: "A task becomes overdue" },
  { type: "schedule", label: "On a schedule" },
  { type: "message.posted", label: "A message is posted" },
  { type: "observation.recorded", label: "A number is recorded" },
  { type: "date_field_reached", label: "A date is reached" },
  { type: "task.file_changed", label: "A file changes" },
  { type: "task.linked", label: "A task is linked" },
];
/** Triggers that read one of the board's fields (a company-wide rule names none). */
const FIELD_TRIGGERS = new Set<Trigger["type"]>(["task.field_changed", "date_field_reached"]);
const FILE_OPS = [
  ["added", "added"],
  ["renamed", "renamed"],
  ["moved", "moved"],
  ["cover_set", "made the cover"],
  ["cover_cleared", "unmade as cover"],
  ["trashed", "trashed"],
  ["restored", "restored"],
] as const;
const RELATIONS = [
  ["relates_to", "relates to"],
  ["duplicates", "duplicates"],
  ["duplicated_by", "duplicated by"],
  ["blocks", "blocks"],
  ["blocked_by", "blocked by"],
] as const;
const CONDITIONS: { type: Condition["type"]; label: string }[] = [
  { type: "status_is", label: "Status is" },
  { type: "priority_is", label: "Priority is" },
  { type: "has_tag", label: "Has the tag" },
  { type: "title_contains", label: "Title contains" },
  { type: "field_equals", label: "Field is" },
  { type: "metric_above", label: "The number is above" },
  { type: "metric_below", label: "The number is below" },
];
const METRIC_CONDITIONS = new Set<Condition["type"]>(["metric_above", "metric_below"]);
/**
 * Field types "Field is" compares one stored value of exactly: lists, computed fields and the
 * structured ones (a date's stored timestamp, a currency's amount and code) are left out.
 */
const COMPARABLE = new Set(["text", "number", "select", "seat", "checkbox", "url"]);
const NUMERIC = new Set(["number"]);

/** A typed value from the editor's text, as the field stores it (empty: no value). */
export function fieldValue(
  type: string | undefined,
  raw: string,
): string | number | boolean | null {
  if (raw === "") return null;
  if (type && NUMERIC.has(type)) {
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }
  if (type === "checkbox") return raw === "true";
  return raw;
}
const ACTIONS: { type: Action["type"]; label: string }[] = [
  { type: "set_status", label: "Set status" },
  { type: "set_priority", label: "Set priority" },
  { type: "add_tag", label: "Add a tag" },
  { type: "add_comment", label: "Comment" },
  { type: "assign", label: "Assign a seat" },
  { type: "wake_agent", label: "Wake an agent" },
  { type: "wake_owner", label: "Wake the owner (DE)" },
  { type: "create_task", label: "Create a task" },
  { type: "move_to_board", label: "Move to a board" },
];
/** Actions that need no task (they work under any trigger). */
const TASKLESS = new Set<Action["type"]>(["create_task", "wake_agent", "wake_owner"]);
const NO_TASK = new Set<Trigger["type"]>(["schedule", "message.posted", "observation.recorded"]);

type Pickers = {
  boards: { id: string; name: string; status_set: { key: string; name: string }[] }[];
  seats: { id: string; title: string }[];
  tags: { id: string; name: string }[];
  /** The fields of the rule's board (a company-wide rule names none). */
  fields: { id: string; name: string; type: string; choices: { key: string; name: string }[] }[];
  channels: { id: string; name: string }[];
  metrics: { id: string; name: string }[];
};

/** A blank part of each kind, its ids defaulted to the first option. */
function blankTrigger(type: Trigger["type"], p: Pickers): Trigger {
  switch (type) {
    case "task.due_soon":
      return { type, hours: 24 };
    case "schedule":
      return { type, at: "09:00", weekdays: [1, 2, 3, 4, 5] };
    case "task.field_changed":
      return { type, field_id: p.fields[0]?.id ?? "" };
    case "message.posted":
      return { type, channel_id: p.channels[0]?.id ?? "" };
    case "observation.recorded":
      return { type, metric_def_id: p.metrics[0]?.id ?? "" };
    case "date_field_reached":
      return {
        type,
        field_id: p.fields.find((f) => f.type === "date")?.id ?? "",
        offset_hours: 0,
      };
    default:
      return { type } as Trigger;
  }
}
function blankCondition(type: Condition["type"], p: Pickers, statuses: string[]): Condition {
  switch (type) {
    case "status_is":
      return { type, statuses: [statuses[0] ?? "todo"] };
    case "priority_is":
      return { type, priorities: [1] };
    case "has_tag":
      return { type, tag_id: p.tags[0]?.id ?? "" };
    case "field_equals":
      return {
        type,
        field_id: p.fields.find((f) => COMPARABLE.has(f.type))?.id ?? "",
        value: null,
      };
    case "metric_above":
    case "metric_below":
      return { type, value: "0" };
    default:
      return { type: "title_contains", text: "" };
  }
}
function blankAction(
  type: Action["type"],
  p: Pickers,
  statuses: string[],
  board: string | null,
): Action {
  switch (type) {
    case "set_status":
      return { type, status: statuses[0] ?? "todo" };
    case "set_priority":
      return { type, priority: 2 };
    case "add_tag":
      return { type, tag_id: p.tags[0]?.id ?? "" };
    case "add_comment":
      return { type, text: "" };
    case "assign":
      return { type, seat_id: p.seats[0]?.id ?? "" };
    case "wake_agent":
      return { type, seat_id: p.seats[0]?.id ?? "", brief: "" };
    case "wake_owner":
      return { type, brief: "" };
    case "create_task":
      return { type, board_id: board ?? p.boards[0]?.id ?? "", title: "" };
    default:
      return { type: "move_to_board", board_id: p.boards.find((b) => b.id !== board)?.id ?? "" };
  }
}

export type BuilderStart =
  | { kind: "new"; boardId: string | null; draft?: Partial<Spec> }
  | { kind: "edit"; rule: Rule };

const BUDGET = 200;

function initialSpec(start: BuilderStart, company: CompanyAccess): Spec {
  if (start.kind === "edit")
    return {
      name: start.rule.name,
      scope: start.rule.scope,
      board_id: start.rule.board_id,
      trigger: start.rule.trigger,
      conditions: start.rule.conditions,
      actions: start.rule.actions,
      effect_budget_per_hour: start.rule.effect_budget_per_hour,
    };
  return {
    name: "",
    scope: start.boardId ? "board" : isManager(company) ? "company" : "board",
    board_id: start.boardId,
    trigger: { type: "task.created" },
    conditions: [],
    actions: [],
    effect_budget_per_hour: BUDGET,
    ...start.draft,
  };
}

export function RuleBuilder({
  api,
  company,
  start,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  start: BuilderStart;
  onClose: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const ids = {
    name: useId(),
    board: useId(),
    trigger: useId(),
    describe: useId(),
    budget: useId(),
  };
  const boards = useQuery(boardsQuery(api, co));
  const seats = useInfiniteQuery(seatsQuery(api, co));
  const tags = useQuery(tagsQuery(api, co));
  const channels = useQuery(channelsQuery(api, co));
  const metrics = useInfiniteQuery(metricsQuery(api, co));
  const [spec, setSpec] = useState<Spec>(() => initialSpec(start, company));
  const fields = useQuery({
    ...fieldsQuery(api, co, spec.board_id ?? ""),
    enabled: Boolean(spec.board_id),
  });
  const pickers: Pickers = {
    boards: (boards.data?.data ?? []).map((b) => ({
      id: b.id,
      name: b.name,
      status_set: b.status_set,
    })),
    seats: (seats.data?.pages.flatMap((p) => p.data) ?? [])
      .filter((s) => s.status !== "closed")
      .map((s) => ({ id: s.id, title: s.title })),
    tags: tags.data ?? [],
    fields: (spec.board_id ? (fields.data?.data ?? []) : []).map((f) => ({
      id: f.id,
      name: f.name,
      type: f.type,
      choices: (f.options.choices ?? []).map((c) => ({ key: c.key, name: c.name })),
    })),
    channels: (channels.data?.data ?? [])
      .filter((c) => !c.archived_at)
      .map((c) => ({ id: c.id, name: `#${c.name}` })),
    metrics: (metrics.data?.pages.flatMap((p) => p.data) ?? []).map((m) => ({
      id: m.id,
      name: m.name,
    })),
  };
  // a board's rule starts on the first board the picker offers (never a board shown but unset)
  const firstBoard = pickers.boards[0]?.id ?? null;
  useEffect(() => {
    if (spec.scope === "board" && !spec.board_id && firstBoard)
      setSpec((s) => (s.scope === "board" && !s.board_id ? { ...s, board_id: firstBoard } : s));
  }, [spec.scope, spec.board_id, firstBoard]);
  const [describe, setDescribe] = useState("");
  const [unmatched, setUnmatched] = useState<string[]>([]);
  const board = pickers.boards.find((b) => b.id === spec.board_id) ?? null;
  const statuses = board?.status_set ?? [];
  const statusKeys = statuses.map((s) => s.key);
  const taskTrigger = !NO_TASK.has(spec.trigger.type);
  const set = (patch: Partial<Spec>) => setSpec((s) => ({ ...s, ...patch }));

  // B1-DE-RULES: ready sentences for this board (the recipe gallery in plain words)
  const recipes = useQuery({
    queryKey: [...processKeys.rules(co), "recipes", spec.board_id ?? ""],
    queryFn: () =>
      api.client.automations.recipes({
        params: { co },
        query: spec.board_id ? { board_id: spec.board_id } : {},
      }),
    enabled: start.kind === "new",
    staleTime: 60_000,
  });
  const draft = useMutation({
    mutationFn: (text: string) =>
      api.client.automations.draft({
        params: { co },
        body: { text, ...(spec.board_id ? { board_id: spec.board_id } : {}) },
      }),
    onSuccess: (d) => {
      setSpec((s) => ({
        ...s,
        name: s.name || d.name,
        ...(d.trigger ? { trigger: d.trigger } : {}),
        conditions: d.conditions.length ? d.conditions : s.conditions,
        actions: d.actions.length ? d.actions : s.actions,
      }));
      setUnmatched(d.unmatched);
    },
  });
  const save = useMutation({
    mutationFn: async () => {
      if (start.kind === "edit")
        return api.client.automations.update({
          params: { co, id: start.rule.id },
          ifMatch: start.rule.version,
          body: {
            name: spec.name,
            trigger: spec.trigger,
            conditions: spec.conditions,
            actions: spec.actions,
            ...(spec.effect_budget_per_hour
              ? { effect_budget_per_hour: spec.effect_budget_per_hour }
              : {}),
          },
        });
      return api.client.automations.create({ params: { co }, body: spec });
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: processKeys.rules(co) });
      toast.add({
        title: r.proposed
          ? "Sent for approval"
          : start.kind === "edit"
            ? "Automation saved"
            : "Automation created",
        type: "success",
      });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  const statusSelect = (value: string, onChange: (v: string) => void, label: string) => (
    <NativeSelect aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {statuses.map((s) => (
        <NativeSelectOption key={s.key} value={s.key}>
          {s.name}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  );
  const pick = (
    options: { id: string; label: string }[],
    value: string,
    onChange: (v: string) => void,
    label: string,
  ) => (
    <NativeSelect aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <NativeSelectOption key={o.id} value={o.id}>
          {o.label}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  );
  const seatOptions = pickers.seats.map((s) => ({ id: s.id, label: s.title }));
  const tagOptions = pickers.tags.map((t) => ({ id: t.id, label: t.name }));
  const boardOptions = pickers.boards.map((b) => ({ id: b.id, label: b.name }));
  const fieldOptions = pickers.fields.map((f) => ({ id: f.id, label: f.name }));
  const dateOptions = pickers.fields
    .filter((f) => f.type === "date")
    .map((f) => ({ id: f.id, label: f.name }));
  const comparableOptions = pickers.fields
    .filter((f) => COMPARABLE.has(f.type))
    .map((f) => ({ id: f.id, label: f.name }));
  const channelOptions = pickers.channels.map((c) => ({ id: c.id, label: c.name }));
  const metricOptions = pickers.metrics.map((m) => ({ id: m.id, label: m.name }));
  // the conditions the trigger can read: the task's for a task trigger, the number's for
  // "a number is recorded", none otherwise
  // statuses are a board's: a company-wide rule offers no status condition or action
  const conditionOptions = CONDITIONS.filter(
    (c) =>
      (c.type !== "status_is" || spec.board_id !== null) &&
      (spec.trigger.type === "observation.recorded"
        ? METRIC_CONDITIONS.has(c.type)
        : taskTrigger && !METRIC_CONDITIONS.has(c.type)),
  );
  const boardMissing = spec.scope === "board" && !spec.board_id;

  function triggerFields(t: Trigger) {
    switch (t.type) {
      case "task.status_changed":
        return (
          <NativeSelect
            aria-label="To status"
            value={t.to ?? ""}
            onChange={(e) =>
              set({
                trigger: e.target.value
                  ? { type: "task.status_changed", to: e.target.value }
                  : { type: "task.status_changed" },
              })
            }
          >
            <NativeSelectOption value="">to any status</NativeSelectOption>
            {statuses.map((s) => (
              <NativeSelectOption key={s.key} value={s.key}>
                to {s.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        );
      case "task.tagged":
        return (
          <NativeSelect
            aria-label="With the tag"
            value={t.tag_id ?? ""}
            onChange={(e) =>
              set({
                trigger: e.target.value
                  ? { type: "task.tagged", tag_id: e.target.value }
                  : { type: "task.tagged" },
              })
            }
          >
            <NativeSelectOption value="">with any tag</NativeSelectOption>
            {pickers.tags.map((x) => (
              <NativeSelectOption key={x.id} value={x.id}>
                {x.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        );
      case "task.due_soon":
        return (
          <span className="flex items-center gap-2 text-sm">
            within
            <Input
              aria-label="Hours before it is due"
              type="number"
              min={1}
              max={168}
              className="w-20"
              value={t.hours}
              onChange={(e) =>
                set({ trigger: { type: "task.due_soon", hours: Number(e.target.value) || 1 } })
              }
            />
            hours
          </span>
        );
      case "task.field_changed":
        return pick(
          fieldOptions,
          t.field_id,
          (v) => set({ trigger: { type: "task.field_changed", field_id: v } }),
          "Field",
        );
      case "message.posted":
        return pick(
          channelOptions,
          t.channel_id,
          (v) => set({ trigger: { type: "message.posted", channel_id: v } }),
          "In channel",
        );
      case "observation.recorded":
        return pick(
          metricOptions,
          t.metric_def_id,
          (v) => set({ trigger: { type: "observation.recorded", metric_def_id: v } }),
          "Number",
        );
      case "schedule":
        return (
          <span className="flex flex-wrap items-center gap-2 text-sm">
            at
            <Input
              aria-label="Time"
              type="time"
              className="w-28"
              value={t.at}
              onChange={(e) => set({ trigger: { ...t, at: e.target.value.slice(0, 5) } })}
            />
            <NativeSelect
              aria-label="Days"
              value={t.weekdays ? "weekdays" : "every"}
              onChange={(e) =>
                set({
                  trigger:
                    e.target.value === "weekdays"
                      ? { type: "schedule", at: t.at, weekdays: [1, 2, 3, 4, 5] }
                      : { type: "schedule", at: t.at },
                })
              }
            >
              <NativeSelectOption value="weekdays">on weekdays</NativeSelectOption>
              <NativeSelectOption value="every">every day</NativeSelectOption>
            </NativeSelect>
          </span>
        );
      case "date_field_reached": {
        const days = Math.abs(t.offset_hours) / 24;
        const side = t.offset_hours < 0 ? "before" : t.offset_hours > 0 ? "after" : "on";
        const put = (n: number, s: string) =>
          set({
            trigger: {
              ...t,
              offset_hours: s === "on" ? 0 : Math.round(n * 24) * (s === "before" ? -1 : 1),
            },
          });
        return (
          <span className="flex flex-wrap items-center gap-2 text-sm">
            {side === "on" ? null : (
              <>
                <Input
                  aria-label="Days"
                  type="number"
                  min={1}
                  max={90}
                  className="w-20"
                  value={days || 1}
                  onChange={(e) => put(Math.max(1, Number(e.target.value) || 1), side)}
                />
                days
              </>
            )}
            <NativeSelect
              aria-label="When"
              value={side}
              onChange={(e) => put(days || 1, e.target.value)}
            >
              <NativeSelectOption value="on">on</NativeSelectOption>
              <NativeSelectOption value="before">before</NativeSelectOption>
              <NativeSelectOption value="after">after</NativeSelectOption>
            </NativeSelect>
            {pick(
              dateOptions,
              t.field_id,
              (v) => set({ trigger: { ...t, field_id: v } }),
              "Date field",
            )}
          </span>
        );
      }
      case "task.file_changed":
        return (
          <NativeSelect
            aria-label="Change"
            value={t.op ?? ""}
            onChange={(e) =>
              set({
                trigger: e.target.value
                  ? {
                      type: "task.file_changed",
                      op: e.target.value as (typeof FILE_OPS)[number][0],
                    }
                  : { type: "task.file_changed" },
              })
            }
          >
            <NativeSelectOption value="">any change</NativeSelectOption>
            {FILE_OPS.map(([k, label]) => (
              <NativeSelectOption key={k} value={k}>
                {label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        );
      case "task.linked":
        return (
          <NativeSelect
            aria-label="Link kind"
            value={t.relation ?? ""}
            onChange={(e) =>
              set({
                trigger: e.target.value
                  ? {
                      type: "task.linked",
                      relation: e.target.value as (typeof RELATIONS)[number][0],
                    }
                  : { type: "task.linked" },
              })
            }
          >
            <NativeSelectOption value="">any link</NativeSelectOption>
            {RELATIONS.map(([k, label]) => (
              <NativeSelectOption key={k} value={k}>
                {label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        );
      default:
        return null;
    }
  }

  function conditionFields(c: Condition, i: number) {
    const put = (next: Condition) =>
      set({ conditions: spec.conditions.map((x, j) => (j === i ? next : x)) });
    switch (c.type) {
      case "status_is":
        return statusSelect(
          c.statuses[0] ?? "",
          (v) => put({ type: "status_is", statuses: [v] }),
          "Status",
        );
      case "priority_is":
        return (
          <NativeSelect
            aria-label="Priority"
            value={String(c.priorities[0] ?? 1)}
            onChange={(e) => put({ type: "priority_is", priorities: [Number(e.target.value)] })}
          >
            {PRIORITIES.map((p, n) => (
              <NativeSelectOption key={p} value={String(n)}>
                {p}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        );
      case "has_tag":
        return pick(tagOptions, c.tag_id, (v) => put({ type: "has_tag", tag_id: v }), "Tag");
      case "title_contains":
        return (
          <Input
            aria-label="Text"
            value={c.text}
            onChange={(e) => put({ type: "title_contains", text: e.target.value })}
          />
        );
      case "field_equals": {
        const f = pickers.fields.find((x) => x.id === c.field_id);
        const text = c.value === null ? "" : String(c.value);
        const setValue = (raw: string) => put({ ...c, value: fieldValue(f?.type, raw) });
        return (
          <span className="flex min-w-0 flex-1 gap-2">
            {pick(
              comparableOptions,
              c.field_id,
              (v) => put({ type: "field_equals", field_id: v, value: null }),
              "Field",
            )}
            {f?.type === "checkbox" || f?.type === "select" ? (
              <NativeSelect
                aria-label="Value"
                value={text}
                onChange={(e) => setValue(e.target.value)}
              >
                <NativeSelectOption value="">Empty</NativeSelectOption>
                {f.type === "checkbox" ? (
                  <>
                    <NativeSelectOption value="true">Checked</NativeSelectOption>
                    <NativeSelectOption value="false">Unchecked</NativeSelectOption>
                  </>
                ) : (
                  f.choices.map((o) => (
                    <NativeSelectOption key={o.key} value={o.key}>
                      {o.name}
                    </NativeSelectOption>
                  ))
                )}
              </NativeSelect>
            ) : (
              <Input
                aria-label="Value"
                placeholder="Empty"
                type={f && NUMERIC.has(f.type) ? "number" : "text"}
                value={text}
                onChange={(e) => setValue(e.target.value)}
              />
            )}
          </span>
        );
      }
      case "metric_above":
      case "metric_below":
        return (
          <Input
            aria-label="Value"
            inputMode="decimal"
            className="w-32"
            value={c.value}
            onChange={(e) => put({ type: c.type, value: e.target.value.trim() || "0" })}
          />
        );
    }
  }

  function actionFields(x: Action, i: number) {
    const put = (next: Action) =>
      set({ actions: spec.actions.map((y, j) => (j === i ? next : y)) });
    switch (x.type) {
      case "set_status":
        return statusSelect(x.status, (v) => put({ type: "set_status", status: v }), "Status");
      case "set_priority":
        return (
          <NativeSelect
            aria-label="Priority"
            value={String(x.priority)}
            onChange={(e) => put({ type: "set_priority", priority: Number(e.target.value) })}
          >
            {PRIORITIES.map((p, n) => (
              <NativeSelectOption key={p} value={String(n)}>
                {p}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        );
      case "add_tag":
        return pick(tagOptions, x.tag_id, (v) => put({ type: "add_tag", tag_id: v }), "Tag");
      case "add_comment":
        return (
          <Input
            aria-label="Comment"
            placeholder="What to say"
            value={x.text}
            onChange={(e) => put({ type: "add_comment", text: e.target.value })}
          />
        );
      case "assign":
        return pick(seatOptions, x.seat_id, (v) => put({ type: "assign", seat_id: v }), "Seat");
      case "wake_agent":
        return (
          <span className="flex min-w-0 flex-1 gap-2">
            {pick(seatOptions, x.seat_id, (v) => put({ ...x, seat_id: v }), "Agent seat")}
            <Input
              aria-label="Brief"
              placeholder="What to do"
              value={x.brief}
              onChange={(e) => put({ ...x, brief: e.target.value })}
            />
          </span>
        );
      case "wake_owner":
        return (
          <Input
            aria-label="Brief"
            placeholder="What to look at"
            value={x.brief}
            onChange={(e) => put({ type: "wake_owner", brief: e.target.value })}
          />
        );
      case "create_task":
        return (
          <span className="flex min-w-0 flex-1 gap-2">
            <Input
              aria-label="Task title"
              placeholder="Title"
              value={x.title}
              onChange={(e) => put({ ...x, title: e.target.value })}
            />
            {pick(boardOptions, x.board_id, (v) => put({ ...x, board_id: v }), "On board")}
            <NativeSelect
              aria-label="Assigned to"
              value={x.assignee_seat_id ?? ""}
              onChange={(e) => {
                const { assignee_seat_id: _drop, ...rest } = x;
                put(e.target.value ? { ...rest, assignee_seat_id: e.target.value } : rest);
              }}
            >
              <NativeSelectOption value="">Unassigned</NativeSelectOption>
              {seatOptions.map((o) => (
                <NativeSelectOption key={o.id} value={o.id}>
                  {o.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </span>
        );
      case "move_to_board":
        return pick(
          boardOptions,
          x.board_id,
          (v) => put({ type: "move_to_board", board_id: v }),
          "Board",
        );
    }
  }

  const row = (label: string, fields: React.ReactNode, onRemove: () => void, key: string) => (
    <li key={key} className="flex items-center gap-2">
      <span className="w-32 shrink-0 text-sm">{label}</span>
      <span className="flex min-w-0 flex-1 items-center gap-2">{fields}</span>
      <Button variant="ghost" size="icon-sm" aria-label={`Remove ${label}`} onClick={onRemove}>
        <XIcon aria-hidden="true" />
      </Button>
    </li>
  );

  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-2xl" data-slot="rule-builder">
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>
              {start.kind === "edit" ? "Edit automation" : "New automation"}
            </DialogTitle>
            <DialogDescription>
              When something happens, if it fits, then act. It runs with your access, as you.
            </DialogDescription>
          </DialogHeader>

          {start.kind === "new" ? (
            <Field>
              <FieldLabel htmlFor={ids.describe}>
                <SparklesIcon aria-hidden="true" className="size-4" /> Describe it
              </FieldLabel>
              <span className="flex gap-2">
                <Textarea
                  id={ids.describe}
                  rows={2}
                  placeholder="When a task moves to Done, set priority to low and comment “Shipped”"
                  value={describe}
                  onChange={(e) => setDescribe(e.target.value)}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={!describe.trim() || draft.isPending}
                  onClick={() => draft.mutate(describe)}
                >
                  Draft it
                </Button>
              </span>
              {recipes.data?.data.length ? (
                <ul aria-label="Ideas" className="m-0 flex list-none flex-wrap gap-1 p-0">
                  {recipes.data.data.map((r) => (
                    <li key={r.id}>
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        className="h-auto max-w-full whitespace-normal py-0.5 text-left"
                        disabled={draft.isPending}
                        onClick={() => {
                          setDescribe(r.text);
                          draft.mutate(r.text);
                        }}
                      >
                        {r.text}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {unmatched.length ? (
                <Notice tone="warning">
                  Not understood, finish below: {unmatched.map((u) => `“${u}”`).join(", ")}
                </Notice>
              ) : null}
            </Field>
          ) : null}

          <span className="grid gap-3 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor={ids.name}>Name</FieldLabel>
              <Input
                id={ids.name}
                required
                maxLength={200}
                value={spec.name}
                onChange={(e) => set({ name: e.target.value })}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.board}>Where</FieldLabel>
              <NativeSelect
                id={ids.board}
                disabled={start.kind === "edit"}
                value={spec.board_id ?? ""}
                onChange={(e) =>
                  set(
                    e.target.value
                      ? { scope: "board", board_id: e.target.value }
                      : { scope: "company", board_id: null },
                  )
                }
              >
                {isManager(company) || spec.scope === "company" ? (
                  <NativeSelectOption value="">Every board (company-wide)</NativeSelectOption>
                ) : null}
                {pickers.boards.map((b) => (
                  <NativeSelectOption key={b.id} value={b.id}>
                    {b.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          </span>

          <section aria-labelledby={ids.trigger} className="flex flex-col gap-2">
            <h3 id={ids.trigger} className="text-xs font-medium uppercase text-muted-foreground">
              When
            </h3>
            <span className="flex flex-wrap items-center gap-2">
              <NativeSelect
                aria-label="Trigger"
                value={spec.trigger.type}
                onChange={(e) => {
                  const trigger = blankTrigger(e.target.value as Trigger["type"], pickers);
                  // parts the new trigger cannot read go with the old one
                  const task = !NO_TASK.has(trigger.type);
                  const metric = trigger.type === "observation.recorded";
                  set({
                    trigger,
                    conditions: spec.conditions.filter((c) =>
                      metric
                        ? METRIC_CONDITIONS.has(c.type)
                        : task && !METRIC_CONDITIONS.has(c.type),
                    ),
                    actions: task ? spec.actions : spec.actions.filter((a) => TASKLESS.has(a.type)),
                  });
                }}
              >
                {TRIGGERS.filter((t) => !FIELD_TRIGGERS.has(t.type) || spec.board_id !== null).map(
                  (t) => (
                    <NativeSelectOption key={t.type} value={t.type}>
                      {t.label}
                    </NativeSelectOption>
                  ),
                )}
              </NativeSelect>
              {triggerFields(spec.trigger)}
            </span>
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-medium uppercase text-muted-foreground">If (optional)</h3>
            <ul className="flex flex-col gap-2">
              {spec.conditions.map((c, i) =>
                row(
                  CONDITIONS.find((x) => x.type === c.type)?.label ?? c.type,
                  conditionFields(c, i),
                  () => set({ conditions: spec.conditions.filter((_, j) => j !== i) }),
                  `c${i}`,
                ),
              )}
            </ul>
            {conditionOptions.length ? (
              <AddPart
                key={spec.trigger.type}
                label="Add condition"
                options={conditionOptions}
                onAdd={(type) =>
                  set({
                    conditions: [...spec.conditions, blankCondition(type, pickers, statusKeys)],
                  })
                }
              />
            ) : null}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-medium uppercase text-muted-foreground">Then</h3>
            <ul className="flex flex-col gap-2">
              {spec.actions.map((x, i) =>
                row(
                  ACTIONS.find((y) => y.type === x.type)?.label ?? x.type,
                  actionFields(x, i),
                  () => set({ actions: spec.actions.filter((_, j) => j !== i) }),
                  `a${i}`,
                ),
              )}
            </ul>
            <AddPart
              label="Add action"
              options={ACTIONS.filter(
                (a) =>
                  ((a.type !== "set_status" && a.type !== "wake_owner") ||
                    spec.board_id !== null) &&
                  (taskTrigger || TASKLESS.has(a.type)),
              )}
              onAdd={(type) =>
                set({
                  actions: [...spec.actions, blankAction(type, pickers, statusKeys, spec.board_id)],
                })
              }
            />
          </section>

          <Field orientation="horizontal">
            <FieldLabel htmlFor={ids.budget}>At most</FieldLabel>
            <Input
              id={ids.budget}
              type="number"
              min={1}
              max={10_000}
              className="w-24"
              value={spec.effect_budget_per_hour ?? BUDGET}
              onChange={(e) =>
                set({
                  effect_budget_per_hour: Math.min(
                    10_000,
                    Math.max(1, Math.round(Number(e.target.value)) || 1),
                  ),
                })
              }
            />
            <span className="text-sm text-muted-foreground">
              effects an hour (past it the rule pauses itself)
            </span>
          </Field>

          {boardMissing ? <Notice tone="warning">Choose the board this rule is for.</Notice> : null}
          {save.isError ? (
            <Notice tone="danger">{problemText(save.error, "The automation was not saved")}</Notice>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={save.isPending || !spec.actions.length || !spec.name.trim() || boardMissing}
            >
              {start.kind === "edit" ? "Save" : "Create automation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddPart<T extends string>({
  label,
  options,
  onAdd,
}: {
  label: string;
  options: { type: T; label: string }[];
  onAdd: (type: T) => void;
}) {
  const [picked, setType] = useState<T>(options[0]?.type as T);
  // the options follow the trigger: a choice no longer offered falls back to the first one
  const type = options.some((o) => o.type === picked) ? picked : (options[0]?.type as T);
  return (
    <span className="flex items-center gap-2">
      <NativeSelect
        aria-label={`${label}: kind`}
        value={type}
        onChange={(e) => setType(e.target.value as T)}
      >
        {options.map((o) => (
          <NativeSelectOption key={o.type} value={o.type}>
            {o.label}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      <Button type="button" variant="outline" size="sm" onClick={() => onAdd(type)}>
        <PlusIcon aria-hidden="true" />
        {label}
      </Button>
    </span>
  );
}
